import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import PageHeader from '@shared/components/PageHeader'
import SEO from '@shared/components/SEO'


const PRINCIPLES = [
    {
        title: 'Institutional Reform',
        text: 'Rethinking public institutions and public law to make them fit for contemporary democratic life.'
    },
    {
        title: 'Transparency & Accountability',
        text: 'Procedures and rules that lead to greater efficiency, openness and answerability in governance.'
    },
    {
        title: 'Inclusive Participation',
        text: 'Strengthening democracy and civil society so that governance is participatory and broad-based.'
    }
]

function StatementBlock({
    numeral,
    label,
    children,
    delay = 0
}: {
    numeral: string
    label: string
    children: React.ReactNode
    delay?: number
}) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55, delay }}
            className="grid md:grid-cols-[5rem_1fr] gap-x-8 gap-y-4"
        >
            <div className="flex md:flex-col items-baseline md:items-end gap-3 md:gap-2 md:text-right">
                <div
                    className="font-display leading-none"
                    style={{
                        fontSize: 'clamp(2.6rem, 5vw, 3.6rem)',
                        color: 'var(--color-gold-500)',
                        letterSpacing: '-0.03em'
                    }}
                >
                    {numeral}
                </div>
            </div>
            <div>
                <div
                    className="kicker"
                    style={{ color: 'var(--color-accent-500)' }}
                >
                    {label}
                </div>
                <div
                    className="prose-body mt-4"
                    style={{
                        fontSize: 'clamp(1.15rem, 2vw, 1.4rem)',
                        lineHeight: 1.6,
                        color: 'var(--color-ink-700)'
                    }}
                >
                    {children}
                </div>
            </div>
        </motion.div>
    )
}

export default function VisionMission() {
    return (
        <>
            <SEO
                title="Vision & Mission"
                description="The vision and mission of the Centre for Study of Politics and Governance — institutional reform, transparency, accountability and inclusive democratic governance."
                path="/vision-mission"
            />
            <PageHeader
                eyebrow="The Centre"
                title="Vision & Mission"
                lede="What the Centre for Study of Politics and Governance stands for, and the work it sets out to do."
            />

            <section className="section">
                <div className="container-prose">
                    <div className="grid gap-14 md:gap-20">
                        <StatementBlock numeral="I" label="Our Vision">
                            The Centre for the Study of Politics and Governance, in its
                            various forms and at different sites, is central to several
                            contemporary issues: the reform of public institutions and
                            public law; the creation and establishment of procedures and
                            rules that lead to greater{' '}
                            <span style={{ color: 'var(--color-accent-600)' }}>
                                efficiency, transparency and accountability
                            </span>
                            ; and the challenge of making governance more inclusive and
                            participatory through the strengthening of democracy and civil
                            society.
                        </StatementBlock>

                        <StatementBlock numeral="II" label="Our Mission" delay={0.05}>
                            CSPG provides consultancy services, research assistance and
                            project appraisals to scholars, researchers and organisations in
                            consonance with the basic ideals of the Centre. It presents
                            opinion and views at appropriate public forums — for example, at
                            public hearings of parliamentary committees and bodies inviting
                            public opinion.
                        </StatementBlock>
                    </div>

                    <motion.figure
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="my-16 md:my-20 text-center"
                    >
                        <hr className="rule-double mb-9" />
                        <blockquote
                            className="font-display mx-auto"
                            style={{
                                maxWidth: '46rem',
                                fontSize: 'clamp(1.4rem, 3vw, 2rem)',
                                lineHeight: 1.32,
                                letterSpacing: '-0.02em',
                                color: 'var(--color-ink-700)'
                            }}
                        >
                            “Governance is not merely the exercise of power, but the moral
                            responsibility to serve society with justice, integrity and
                            inclusion.”
                        </blockquote>
                        <hr className="rule-double mt-9" />
                    </motion.figure>

                    <div className="kicker kicker--center">Guiding Principles</div>
                    <div className="mt-7 grid sm:grid-cols-3 gap-px" style={{ background: 'var(--color-rule)' }}>
                        {PRINCIPLES.map((p, i) => (
                            <motion.div
                                key={p.title}
                                initial={{ opacity: 0, y: 12 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.45, delay: i * 0.07 }}
                                className="p-7"
                                style={{ background: 'var(--color-paper-50)' }}
                            >
                                <div
                                    className="font-display text-[11px] uppercase font-semibold"
                                    style={{ letterSpacing: '0.16em', color: 'var(--color-gold-700)' }}
                                >
                                    {String(i + 1).padStart(2, '0')}
                                </div>
                                <h3
                                    className="font-display mt-3"
                                    style={{ fontSize: '1.2rem', color: 'var(--color-ink-700)', letterSpacing: '-0.01em' }}
                                >
                                    {p.title}
                                </h3>
                                <p
                                    className="mt-2.5 text-[14.5px] leading-relaxed"
                                    style={{ color: 'var(--color-ink-500)' }}
                                >
                                    {p.text}
                                </p>
                            </motion.div>
                        ))}
                    </div>

                    <div
                        className="mt-16 flex flex-wrap items-center justify-between gap-5 pt-9"
                        style={{ borderTop: '1px solid var(--color-rule-strong)' }}
                    >
                        <p className="lede" style={{ maxWidth: '34rem', fontSize: '1.05rem' }}>
                            Learn more about the people and partnerships behind the Centre.
                        </p>
                        <div className="flex flex-wrap gap-3">
                            <Link to="/about-us" className="btn btn-ghost">
                                About the Centre
                            </Link>
                            <Link to="/board-of-trustees" className="btn btn-gold">
                                Board of Trustees →
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </>
    )
}
